/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// react-countup component
import CountUp from "react-countup";

function Counters() {
  const journeySteps = [
    {
      year: "2008",
      title: "Los Primeros Pasos",
      description: "Comenzamos como una pequeña empresa familiar con una gran visión",
      metric: "1era oficina en Querétaro",
      progress: 25,
      emoji: "🌱",
    },
    {
      year: "2015",
      title: "Expansión Regional",
      description: "Ampliamos nuestro alcance por todo el norte de México",
      metric: "500+ clientes satisfechos",
      progress: 60,
      emoji: "🚀",
    },
    {
      year: "2020",
      title: "Transformación Digital",
      description: "Modernizamos nuestros procesos y servicios",
      metric: "1,500+ proyectos completados",
      progress: 85,
      emoji: "💻",
    },
    {
      year: "2024",
      title: "Líderes del Mercado",
      description: "Nos consolidamos como referente en la región",
      metric: "2,000+ clientes activos",
      progress: 100,
      emoji: "👑",
    },
  ];

  const achievements = [
    { label: "Años de Confianza", value: 15, max: 20, color: "#1976d2" },
    { label: "Satisfacción Cliente", value: 98, max: 100, color: "#388e3c" },
    { label: "Cobertura Norte", value: 100, max: 100, color: "#f57c00" },
    { label: "Crecimiento Anual", value: 85, max: 100, color: "#7b1fa2" },
  ];

  return (
    <MKBox
      component="section"
      py={8}
      sx={{
        background: "linear-gradient(180deg, #e3f2fd 0%, #f3e5f5 100%)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        },
      }}
    >
      <Container>
        <MKBox textAlign="center" mb={6}>
          <MKTypography variant="h2" fontWeight="bold" color="dark" mb={2}>
            El Viaje de Impresia 🎯
          </MKTypography>
          <MKTypography variant="h6" color="text" maxWidth="700px" mx="auto" fontWeight="light">
            Desde 2008 hemos evolucionado constantemente, creando soluciones que transforman
            espacios de trabajo
          </MKTypography>
        </MKBox>

        {/* Timeline Vertical */}
        <MKBox mb={8}>
          <Stepper
            orientation="vertical"
            sx={{
              "& .MuiStepLabel-root": {
                cursor: "pointer",
                transition: "all 0.3s ease",
                padding: "8px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  transform: "scale(1.02)",
                },
              },
              "& .MuiStepIcon-root": {
                fontSize: "2.5rem",
                "&.Mui-active": {
                  color: "#1976d2",
                  filter: "drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))",
                },
                "&.Mui-completed": {
                  color: "#2e7d32",
                  filter: "drop-shadow(0 2px 4px rgba(46, 125, 50, 0.3))",
                },
              },
              "& .MuiStepConnector-line": {
                borderColor: "#e0e0e0",
                borderWidth: "2px",
              },
            }}
          >
            {journeySteps.map((step, index) => (
              <Step key={index} active={true}>
                <StepLabel>
                  <MKBox>
                    <MKBox display="flex" alignItems="center" gap={2} mb={1}>
                      <MKTypography variant="h4" color="primary" fontWeight="bold">
                        {step.year}
                      </MKTypography>
                      <MKTypography variant="h4">{step.emoji}</MKTypography>
                    </MKBox>
                    <MKTypography variant="h5" fontWeight="bold" color="dark">
                      {step.title}
                    </MKTypography>
                  </MKBox>
                </StepLabel>
                <StepContent>
                  <MKBox pl={2} pb={3}>
                    <MKTypography variant="body1" color="text" mb={2}>
                      {step.description}
                    </MKTypography>
                    <Box
                      sx={{
                        background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        borderRadius: 3,
                        p: 3,
                        color: "white",
                        boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <MKTypography
                        variant="body1"
                        fontWeight="bold"
                        sx={{
                          color: "white",
                          textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                          mb: 2,
                        }}
                      >
                        📊 {step.metric}
                      </MKTypography>
                      <LinearProgress
                        variant="determinate"
                        value={step.progress}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: "rgba(255,255,255,0.25)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "white",
                            borderRadius: 5,
                            boxShadow: "0 0 10px rgba(255,255,255,0.5)",
                          },
                        }}
                      />
                    </Box>
                  </MKBox>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </MKBox>

        {/* Dashboard Visual */}
        <MKBox textAlign="center" mb={6}>
          <MKTypography variant="h4" fontWeight="bold" color="dark" mb={4}>
            📈 Nuestro Panel de Resultados
          </MKTypography>

          <Grid container spacing={4}>
            {achievements.map((achievement, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <MKBox
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    background: "white",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <MKTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    {achievement.label}
                  </MKTypography>

                  {/* Gauge Visual */}
                  <MKBox position="relative" mb={3}>
                    <Box
                      sx={{
                        width: 140,
                        height: 140,
                        mx: "auto",
                        position: "relative",
                        borderRadius: "50%",
                        background: `conic-gradient(${achievement.color} ${
                          (achievement.value / achievement.max) * 360
                        }deg, #f5f5f5 0deg)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.08))",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          width: 105,
                          height: 105,
                          borderRadius: "50%",
                          backgroundColor: "white",
                          boxShadow: "inset 0 2px 8px rgba(0,0,0,0.06)",
                        },
                      }}
                    >
                      <MKBox position="relative" textAlign="center" zIndex={1}>
                        <MKTypography
                          variant="h2"
                          fontWeight="bold"
                          sx={{
                            color: "#2c3e50",
                            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            lineHeight: 1,
                          }}
                        >
                          <CountUp end={achievement.value} duration={2} />
                        </MKTypography>
                        <MKTypography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: "#546e7a",
                            mt: 0.5,
                          }}
                        >
                          {achievement.max === 100 ? "%" : `/${achievement.max}`}
                        </MKTypography>
                      </MKBox>
                    </Box>
                  </MKBox>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </MKBox>

        <MKBox
          textAlign="center"
          sx={{
            background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
            borderRadius: 4,
            p: 5,
            color: "white",
            boxShadow: "0 8px 32px rgba(25, 118, 210, 0.25)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <MKTypography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: "white",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              mb: 2,
            }}
          >
            🏆 La Diferencia Impresia
          </MKTypography>
          <MKTypography
            variant="h6"
            sx={{
              color: "white",
              opacity: 0.95,
              fontWeight: 400,
              textShadow: "0 1px 2px rgba(0,0,0,0.1)",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            No somos solo números, somos historias de éxito, relaciones duraderas y soluciones que
            transforman espacios
          </MKTypography>
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default Counters;
